/**
 * CustomerProfile Component
 * 
 * Displays comprehensive customer information including device, location, 
 * marketing attribution, and session behavior data in a clean, organized layout.
 * 
 * Features:
 * - Device and browser information with icons
 * - Geographic location with timezone
 * - Marketing attribution (UTM parameters)
 * - Session behavior analytics
 * - Custom business metadata
 * - Expandable sections for detailed data
 */

import React, { useState } from 'react'
import * as Types from '../../../types'
type CustomerProfileType = Types.CustomerProfile
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card'
import { Badge } from '../../ui/Badge'
import { Button } from '../../ui/Button'
import { 
  User,
  Mail,
  Phone,
  Globe,
  MapPin,
  Clock,
  Monitor,
  Smartphone,
  Tablet,
  TrendingUp,
  Eye,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Calendar,
  BarChart3,
  ExternalLink,
  Info
} from 'lucide-react'
import DeviceBadge from './DeviceBadge'
import LocationBadge from './LocationBadge'
import MarketingBadge from './MarketingBadge'

export interface CustomerProfileProps {
  /** Customer profile data */
  profile: CustomerProfileType
  /** Show detailed technical information */
  showTechnicalDetails?: boolean
  /** Allow expanding/collapsing sections */
  expandable?: boolean
  /** Custom CSS classes */
  className?: string
}

/**
 * Format session duration for display
 */
function formatSessionDuration(seconds?: number): string {
  if (!seconds) return 'Unknown'
  
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
}

/**
 * Format date for display
 */
function formatDate(dateString?: string): string {
  if (!dateString) return 'Unknown'
  
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

/**
 * Get visitor type badge
 */
function getVisitorTypeBadge(isReturning?: boolean, visits?: number) {
  if (isReturning && visits && visits > 1) {
    return (
      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
        <RefreshCw className="w-3 h-3 mr-1" />
        Returning Visitor ({visits} visits)
      </Badge>
    )
  }
  
  return (
    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
      <User className="w-3 h-3 mr-1" />
      First-time Visitor
    </Badge>
  )
}

/**
 * Expandable section component
 */
function ExpandableSection({ 
  title, 
  icon, 
  children, 
  defaultExpanded = false 
}: {
  title: string
  icon: React.ReactNode
  children: React.ReactNode
  defaultExpanded?: boolean
}) {
  
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  
  return (
    <div className="border border-gray-200 rounded-lg">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
          {icon}
          {title}
        </div>
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-500" />
        )}
      </button>
      
      {isExpanded && (
        <div className="px-3 pb-3 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  )
}

/**
 * CustomerProfile component for displaying comprehensive customer data
 */
export function CustomerProfile({
  profile,
  showTechnicalDetails = false,
  expandable = true,
  className = ''
}: CustomerProfileProps) {
  
  const visitorTypeBadge = getVisitorTypeBadge(
    profile.is_returning_visitor, 
    profile.previous_visits
  )
  
  return (
    <div className={`space-y-4 ${className}`}>
      
      {/* Basic Customer Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <User className="w-4 h-4" />
            Customer Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          
          {/* Name and Contact */}
          <div className="grid grid-cols-1 gap-3">
            <div className="flex items-center gap-3">
              <User className="w-4 h-4 text-gray-400" />
              <span className="font-medium">
                {profile.visitor_name || 'Anonymous User'}
              </span>
            </div>
            
            {profile.visitor_email && (
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-600">{profile.visitor_email}</span>
              </div>
            )}
          </div>
          
          {/* Visitor Type */}
          <div className="pt-2">
            {visitorTypeBadge}
          </div>
          
          {/* Quick Overview Badges */}
          <div className="flex flex-wrap gap-2 pt-2">
            <DeviceBadge profile={profile} size="sm" />
            <LocationBadge profile={profile} size="sm" />
            <MarketingBadge profile={profile} size="sm" />
          </div>
        </CardContent>
      </Card>
      
      {/* Session Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart3 className="w-4 h-4" />
            Session Analytics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-gray-600">
                <Clock className="w-3 h-3" />
                <span className="font-medium">Session Duration</span>
              </div>
              <div className="text-gray-900">
                {formatSessionDuration(profile.session_duration)}
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-gray-600">
                <Eye className="w-3 h-3" />
                <span className="font-medium">Pages Visited</span>
              </div>
              <div className="text-gray-900">
                {profile.pages_visited || 'Unknown'}
              </div>
            </div>
            
            {profile.session_start && (
              <div className="space-y-2 col-span-2">
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="w-3 h-3" />
                  <span className="font-medium">Session Started</span>
                </div>
                <div className="text-gray-900">
                  {formatDate(profile.session_start)}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Expandable Sections */}
      {expandable && (
        <div className="space-y-3">
          
          {/* Technical Details */}
          <ExpandableSection
            title="Technical Information"
            icon={<Monitor className="w-4 h-4" />}
            defaultExpanded={showTechnicalDetails}
          >
            <div className="pt-3 space-y-3 text-sm">
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="font-medium text-gray-700 mb-1">Browser</div>
                  <div className="text-gray-600">
                    {profile.browser_name} {profile.browser_version}
                  </div>
                </div>
                
                <div>
                  <div className="font-medium text-gray-700 mb-1">Operating System</div>
                  <div className="text-gray-600">
                    {profile.operating_system || 'Unknown'}
                  </div>
                </div>
                
                <div>
                  <div className="font-medium text-gray-700 mb-1">Device Type</div>
                  <div className="text-gray-600 capitalize">
                    {profile.device_type || 'Unknown'}
                  </div>
                </div>
                
                <div>
                  <div className="font-medium text-gray-700 mb-1">Screen Resolution</div>
                  <div className="text-gray-600">
                    {profile.screen_resolution || 'Unknown'}
                  </div>
                </div>
              </div>
              
              {profile.ip_address && (
                <div>
                  <div className="font-medium text-gray-700 mb-1">IP Address</div>
                  <div className="text-gray-600 font-mono text-xs">
                    {profile.ip_address}
                  </div>
                </div>
              )}
            </div>
          </ExpandableSection>
          
          {/* Marketing Attribution */}
          {(profile.utm_source || profile.utm_campaign || profile.referrer_url) && (
            <ExpandableSection
              title="Marketing Attribution"
              icon={<TrendingUp className="w-4 h-4" />}
            >
              <div className="pt-3 space-y-3 text-sm">
                
                {profile.utm_source && (
                  <div>
                    <div className="font-medium text-gray-700 mb-1">Traffic Source</div>
                    <div className="text-gray-600">{profile.utm_source}</div>
                  </div>
                )}
                
                {profile.utm_medium && (
                  <div>
                    <div className="font-medium text-gray-700 mb-1">Medium</div>
                    <div className="text-gray-600">{profile.utm_medium}</div>
                  </div>
                )}
                
                {profile.utm_campaign && (
                  <div>
                    <div className="font-medium text-gray-700 mb-1">Campaign</div>
                    <div className="text-gray-600">{profile.utm_campaign}</div>
                  </div>
                )}
                
                {profile.utm_term && (
                  <div>
                    <div className="font-medium text-gray-700 mb-1">Search Term</div>
                    <div className="text-gray-600">{profile.utm_term}</div>
                  </div>
                )}
                
                {profile.referrer_url && (
                  <div>
                    <div className="font-medium text-gray-700 mb-1">Referrer</div>
                    <div className="text-gray-600 break-all">
                      <a 
                        href={profile.referrer_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                      >
                        {new URL(profile.referrer_url).hostname}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </ExpandableSection>
          )}
          
          {/* Page Information */}
          {(profile.page_url || profile.page_title) && (
            <ExpandableSection
              title="Page Context"
              icon={<Globe className="w-4 h-4" />}
            >
              <div className="pt-3 space-y-3 text-sm">
                
                {profile.page_title && (
                  <div>
                    <div className="font-medium text-gray-700 mb-1">Page Title</div>
                    <div className="text-gray-600">{profile.page_title}</div>
                  </div>
                )}
                
                {profile.page_url && (
                  <div>
                    <div className="font-medium text-gray-700 mb-1">Page URL</div>
                    <div className="text-gray-600 break-all">
                      <a 
                        href={profile.page_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                      >
                        {profile.page_url}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </ExpandableSection>
          )}
          
          {/* Custom Business Data */}
          {profile.customer_metadata && Object.keys(profile.customer_metadata).length > 0 && (
            <ExpandableSection
              title="Business Data"
              icon={<Info className="w-4 h-4" />}
            >
              <div className="pt-3 space-y-2 text-sm">
                {Object.entries(profile.customer_metadata).map(([key, value]) => (
                  <div key={key}>
                    <div className="font-medium text-gray-700 mb-1 capitalize">
                      {key.replace(/_/g, ' ')}
                    </div>
                    <div className="text-gray-600">
                      {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                    </div>
                  </div>
                ))}
              </div>
            </ExpandableSection>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Compact customer profile for smaller spaces
 */
export function CompactCustomerProfile({ 
  profile 
}: { 
  profile: CustomerProfileType 
}) {
  
  return (
    <div className="p-3 bg-gray-50 rounded-lg space-y-2">
      
      {/* Basic Info */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-gray-400" />
          <span className="font-medium text-sm">
            {profile.visitor_name || 'Anonymous'}
          </span>
        </div>
        
        {profile.is_returning_visitor && (
          <Badge variant="outline" className="text-xs">
            Returning
          </Badge>
        )}
      </div>
      
      {/* Quick Info Badges */}
      <div className="flex flex-wrap gap-1">
        <DeviceBadge profile={profile} size="sm" showTooltip={false} />
        <LocationBadge profile={profile} size="sm" showTooltip={false} />
      </div>
      
      {/* Session Info */}
      {profile.session_duration && (
        <div className="text-xs text-gray-600">
          Session: {formatSessionDuration(profile.session_duration)}
          {profile.pages_visited && ` • ${profile.pages_visited} pages`}
        </div>
      )}
    </div>
  )
}

export default CustomerProfile